import { useQuery } from "@tanstack/react-query";
import { useMemo, useState, RefObject, useEffect } from "react";
import { Control, FieldValues, Path, useController } from "react-hook-form";
import styled, { css } from "styled-components";

import { Input, Label, Larger } from "@/components/Forms";
import Text from "@/components/Text";
import { AudiencableChannel } from "@/types/resources";
import { api } from "@/utils/api";

const InputWrap = styled.div`
  position: relative;
`;

const InputIcon = styled.svg`
  position: absolute;
  top: 0;
  bottom: 0;
  right: 20px;
  margin: auto 0;
`;

const StyledInput = styled(Input)`
  padding-right: 40px;
`;

const Dropdown = styled.div`
  z-index: 2;
  top: calc(100% + 8px);
  position: absolute;
  left: 0;
  width: 100%;
  padding: 8px 4px;
  background: ${({ theme }) => theme.colors.white};
  box-shadow: 0px 0px 8px rgba(37, 40, 43, 0.12);
  border-radius: 4px;
  max-height: 280px;
  overflow-y: auto;
`;

const DropdownOption = styled("button").attrs({ type: "button" })`
  display: flex;
  width: 100%;
  align-items: center;
  padding: 8px 12px;
  border-radius: 4px;
  transition: 0.2s ease-out background-color;
  &:hover {
    background-color: ${({ theme }) => theme.colors.grayscaleLight};
    ${Text} {
      color: ${({ theme }) => theme.colors.black};
    }
  }
  ${Text} {
    text-align: left;
    transition: 0.2s ease-out color;
    flex: 1;
  }
`;

const Checkbox = styled.div<{ $checked: boolean }>`
  margin-left: 12px;
  width: 16px;
  height: 16px;
  border: 2px solid ${({ theme }) => theme.colors.gray};
  border-radius: 2px;
  flex-shrink: 0;
  transition: 0.2s ease-out all;
  position: relative;
  &::after {
    content: "";
    position: absolute;
    display: none;
    top: 1px;
    left: 4px;
    width: 5px;
    height: 9px;
    border: solid ${({ theme }) => theme.colors.white};
    border-width: 0 2px 2px 0;
    transform: rotate(45deg);
  }

  ${({ theme, $checked }) =>
    $checked &&
    css`
      background: ${theme.colors.purple};
      border-color: ${theme.colors.purple};
      &::after {
        display: block;
      }
    `}
`;

interface Props<T extends FieldValues> {
  name: Path<T>;
  control: Control<T>;
  parentRef: RefObject<HTMLFormElement>;
}

const InternalChannelsField = <T extends FieldValues>({
  control,
  name,
  parentRef,
}: Props<T>) => {
  const [search, setSearch] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);

  useEffect(() => {
    if (!showDropdown) return;

    const { current } = parentRef;

    if (!current) return;

    const callback = () => {
      setShowDropdown(false);
      setSearch("");
    };

    current.addEventListener("click", callback);

    return () => current.removeEventListener("click", callback);
  }, [parentRef, showDropdown]);

  const { data: channels } = useQuery(
    [
      "channels",
      {
        forTestingAudiences: true,
      },
    ],
    () =>
      api.get<never, AudiencableChannel[]>("/v1/testing-audiences/channels"),
    {
      staleTime: 120 * 1000,
    }
  );

  const {
    field: { onChange, value },
  } = useController({
    name,
    control,
  });

  const filteredChannels = useMemo(() => {
    if (!search) return channels;

    return channels?.filter((c) =>
      c.name.toLocaleLowerCase().includes(search.toLocaleLowerCase())
    );
  }, [search, channels]);

  const channelIds = useMemo(() => {
    return (value as AudiencableChannel[])?.map((c) => c.id) ?? [];
  }, [value]);

  const checkIfChannelIsSelected = (channelId: number) =>
    channelIds.includes(channelId);

  const onOptionToggle = (channel: AudiencableChannel) => {
    if (checkIfChannelIsSelected(channel.id!)) {
      return onChange(
        value.filter((c: AudiencableChannel) => c.id !== channel.id)
      );
    }
    onChange([...value, channel]);
  };

  const selectedChannelsCount = useMemo(() => {
    return value.filter((c: AudiencableChannel) => c.is_internal).length;
  }, [value]);

  return (
    <Larger>
      <Label>Internal channels</Label>
      <InputWrap>
        <StyledInput
          placeholder={
            selectedChannelsCount
              ? `${selectedChannelsCount} channel${
                  selectedChannelsCount > 1 ? "s" : ""
                } selected`
              : "Select channels"
          }
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onFocus={() => setShowDropdown(true)}
          onClickCapture={(e) => e.nativeEvent.stopImmediatePropagation()}
        />
        <InputIcon
          width="8"
          height="5"
          viewBox="0 0 8 5"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="M4 5L8 0H0L4 5Z" fill="currentColor" />
        </InputIcon>
        {showDropdown && !!filteredChannels?.length && (
          <Dropdown
            onClickCapture={(e) => e.nativeEvent.stopImmediatePropagation()}
          >
            {filteredChannels?.map((channel) => (
              <DropdownOption
                key={channel.external_id}
                onClickCapture={() => onOptionToggle(channel)}
              >
                <Text $type="captionSemibold" as="span" $color="gray">
                  {channel.name}
                </Text>
                <Checkbox $checked={checkIfChannelIsSelected(channel.id!)} />
              </DropdownOption>
            ))}
          </Dropdown>
        )}
      </InputWrap>
    </Larger>
  );
};

export default InternalChannelsField;
